import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Search, Loader2, RefreshCw, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import TicketDialog, { STATUS_META, PRIORITY_META, TYPE_LABEL, formatDate } from '@/components/TicketDialog';

const DEFAULTS = { status: 'open', priority: 'all', school_id: 'all', type: 'all', environment: 'all' };

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
const STATUS_RANK = { open: 0, in_progress: 1, on_hold: 2, resolved: 3 };

const COLUMNS = [
  { key: 'ticket_number', label: 'Ticket' },
  { key: 'subject', label: 'Subject' },
  { key: 'school_id', label: 'School' },
  { key: 'type', label: 'Type' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'environment', label: 'Env' },
  { key: 'created_at', label: 'Raised' },
];

const sortValue = (t, key) => {
  if (key === 'priority') return PRIORITY_RANK[t.priority];
  if (key === 'status') return STATUS_RANK[t.status];
  if (key === 'type') return TYPE_LABEL[t.type];
  if (key === 'school_id') return t.school_id || '';
  if (key === 'subject') return (t.subject || '').toLowerCase();
  return t[key] || '';
};

/** Platform Admin view: every school's tickets, High → Low then oldest first by default. */
export default function TicketsTab({ schools }) {
  const [tickets, setTickets] = useState(null);
  const [filters, setFilters] = useState(DEFAULTS);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(null);           // null keeps the default server order
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState('');
  const [notePrompt, setNotePrompt] = useState(null); // { ticket, status }
  const [noteText, setNoteText] = useState('');

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== 'all') params[k] = v; });
    if (search.trim()) params.search = search.trim();
    try {
      const res = await api.get('/tickets', { params });
      setTickets(res.data);
    } catch {
      setTickets([]);
    }
  }, [filters, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  useEffect(() => {
    window.addEventListener('ticket-created', load);
    return () => window.removeEventListener('ticket-created', load);
  }, [load]);

  const set = (key) => (v) => setFilters(prev => ({ ...prev, [key]: v }));

  const toggleSort = (key) => {
    setSort(prev => {
      if (prev?.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;                                   // third click restores the default order
    });
  };

  const rows = React.useMemo(() => {
    if (!tickets || !sort) return tickets;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...tickets].sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return (a.created_at || '') < (b.created_at || '') ? -1 : 1;
    });
  }, [tickets, sort]);

  const patch = async (ticket, payload, key) => {
    setSavingId(`${ticket.id}-${key}`);
    let ok = false;
    try {
      await api.patch(`/tickets/${ticket.id}`, payload);
      toast.success(`${ticket.ticket_number} updated`);
      await load();
      ok = true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update this ticket');
    }
    setSavingId('');
    return ok;
  };

  const onStatusPick = (ticket, value) => {
    if (value === ticket.status) return;
    if (value === 'on_hold' || value === 'resolved') {
      setNoteText('');
      setNotePrompt({ ticket, status: value });
      return;
    }
    patch(ticket, { status: value }, 'status');
  };

  const confirmWithNote = async () => {
    const { ticket, status } = notePrompt;
    const ok = await patch(ticket, { status, note: noteText.trim() }, 'status');
    if (ok) setNotePrompt(null);
  };

  return (
    <div className="space-y-4 pb-16 lg:pb-0" data-testid="admin-tickets">
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ticket number or subject"
                 className="pl-9 rounded-xl bg-white" data-testid="admin-ticket-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filters.status} onValueChange={set('status')}>
            <SelectTrigger className="w-36 rounded-xl bg-white" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v} data-testid={`admin-filter-status-${v}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority} onValueChange={set('priority')}>
            <SelectTrigger className="w-32 rounded-xl bg-white" data-testid="admin-filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {Object.entries(PRIORITY_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.school_id} onValueChange={set('school_id')}>
            <SelectTrigger className="w-40 rounded-xl bg-white" data-testid="admin-filter-school"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All schools</SelectItem>
              {(schools || []).map(s => <SelectItem key={s.school_id} value={s.school_id}>{s.school_id} · {s.school_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.type} onValueChange={set('type')}>
            <SelectTrigger className="w-36 rounded-xl bg-white" data-testid="admin-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.environment} onValueChange={set('environment')}>
            <SelectTrigger className="w-44 rounded-xl bg-white" data-testid="admin-filter-environment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Both environments</SelectItem>
              <SelectItem value="PREVIEW">Preview</SelectItem>
              <SelectItem value="PRODUCTION">Production</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-xl border-gray-200" data-testid="admin-tickets-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {rows === null ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#F97D6A]" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-sm text-[#6B7280]" data-testid="admin-tickets-empty">
          No tickets match these filters.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-widest text-gray-400">
              <tr>
                {COLUMNS.map(col => {
                  const active = sort?.key === col.key;
                  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
                  return (
                    <th key={col.key} className="text-left py-3 px-4 font-bold">
                      <button onClick={() => toggleSort(col.key)}
                              className={`inline-flex items-center gap-1 uppercase tracking-widest transition-colors ${active ? 'text-[#F97D6A]' : 'hover:text-[#6B7280]'}`}
                              aria-label={`Sort by ${col.label}`}
                              data-testid={`admin-tickets-sort-${col.key}`}>
                        {col.label}
                        <Icon className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-30'}`} />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} onClick={() => setOpenId(t.id)}
                    className="border-t border-gray-100 hover:bg-[#FFF8F7] cursor-pointer transition-colors"
                    data-testid={`admin-ticket-row-${t.ticket_number}`}>
                  <td className="py-3 px-4 font-mono text-xs font-semibold text-[#1F2937] whitespace-nowrap">
                    {t.ticket_number}
                    {t.school_ticket_number && <span className="block text-[10px] text-gray-400 font-normal">{t.school_ticket_number}</span>}
                  </td>
                  <td className="py-3 px-4 text-[#1F2937] min-w-48 max-w-sm break-words">{t.subject}</td>
                  <td className="py-3 px-4 text-xs text-[#6B7280] whitespace-nowrap">{t.school_id || '—'}</td>
                  <td className="py-3 px-4 text-[#6B7280]">{TYPE_LABEL[t.type]}</td>

                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <Select value={t.priority} onValueChange={(v) => v !== t.priority && patch(t, { priority: v }, 'priority')}>
                      <SelectTrigger className={`h-7 w-[104px] rounded-full border-0 px-2.5 text-[10px] font-semibold focus:ring-1 focus:ring-[#F97D6A] ${PRIORITY_META[t.priority].cls}`}
                                     data-testid={`admin-ticket-priority-${t.ticket_number}`}>
                        {savingId === `${t.id}-priority` ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue />}
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRIORITY_META).map(([v, m]) => (
                          <SelectItem key={v} value={v} className="text-xs" data-testid={`admin-ticket-priority-${t.ticket_number}-${v}`}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>

                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <Select value={t.status} onValueChange={(v) => onStatusPick(t, v)}>
                      <SelectTrigger className={`h-7 w-[124px] rounded-full border-0 px-2.5 text-[10px] font-semibold focus:ring-1 focus:ring-[#F97D6A] ${STATUS_META[t.status].cls}`}
                                     data-testid={`admin-ticket-status-${t.ticket_number}`}>
                        {savingId === `${t.id}-status` ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue />}
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([v, m]) => (
                          <SelectItem key={v} value={v} className="text-xs" data-testid={`admin-ticket-status-${t.ticket_number}-${v}`}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>

                  <td className="py-3 px-4 text-[10px] text-[#9CA3AF]">{t.environment}</td>
                  <td className="py-3 px-4 text-xs text-[#9CA3AF] whitespace-nowrap">{formatDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {notePrompt && (
        <Dialog open onOpenChange={() => setNotePrompt(null)}>
          <DialogContent className="sm:max-w-md" data-testid="admin-ticket-note-prompt">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">
                Move {notePrompt.ticket.ticket_number} to {STATUS_META[notePrompt.status].label}
              </DialogTitle>
              <DialogDescription>
                {notePrompt.status === 'on_hold'
                  ? 'Say why this is on hold. The coordinator sees this on the ticket.'
                  : 'Say how this was resolved. The coordinator sees this on the ticket.'}
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label className="text-xs text-gray-500">
                {notePrompt.status === 'on_hold' ? 'Reason' : 'Resolution note'}
              </Label>
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={4}
                        className="mt-1 rounded-xl" data-testid="admin-ticket-note-prompt-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNotePrompt(null)} className="rounded-xl"
                      data-testid="admin-ticket-note-prompt-cancel">Cancel</Button>
              <Button disabled={!noteText.trim() || savingId.endsWith('-status')} onClick={confirmWithNote}
                      className="rounded-xl bg-[#F97D6A] hover:bg-[#e86a57]" data-testid="admin-ticket-note-prompt-confirm">
                {savingId.endsWith('-status') ? <Loader2 className="h-4 w-4 animate-spin" /> : `Move to ${STATUS_META[notePrompt.status].label}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {openId && <TicketDialog ticketId={openId} admin onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
